import { Injectable } from '@angular/core';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { StartupService } from '../service-ui/startup.service';
import {Project} from "../../model/Project";
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {NotificationsService} from "./notifications.service";
import {Observable, of, throwError} from "rxjs";
import {environment} from "../../environments/environment";
import {catchError, map, retry} from "rxjs/operators";
import {AddressValidationResult, ValidationAddress} from "./address-validation.service";
import {EndpointCall, ProjectEndpoints} from "./project-endpoints";

/**
 * Payload accepted by the consolidated
 * `POST /project/createproject` endpoint. See inbox note
 * `2026-08-04-consolidated-createproject-endpoint.md` for full
 * semantics.
 *
 * - `name` is the only required field.
 * - `primaryBook_ID` / `primaryDraft_ID` implicitly join the moved
 *   set: if the caller names a primary but does not include it in
 *   `book_IDs` / `draft_IDs`, the server adds it automatically.
 * - `seedEmptyDraft: true` reproduces the legacy behaviour of
 *   `POST /project/createnewproject/{name}/{folder}` — a fresh
 *   Draft is created and made primary. It only fires when both
 *   `draft_IDs` is empty AND `primaryDraft_ID` is 0 / undefined,
 *   so it can never collide with a caller-supplied primary Draft.
 */
export interface CreateProjectRequest {
  name: string;
  folder?: string;
  primaryBook_ID?: number;
  primaryDraft_ID?: number;
  book_IDs?: number[];
  draft_IDs?: number[];
  seedEmptyDraft?: boolean;
}

/**
 * `data.warnings[]` entry from
 * `POST /project/createproject`. `kind` classifies which sub-step
 * failed. `message` is a human-readable server description of the
 * failure. Additional per-kind fields (`bookId`, `draftId`, etc.)
 * appear on the individual entry — carried opaquely on
 * `[key: string]: any` so the client doesn't have to enumerate
 * every taxonomy value.
 */
export interface CreateProjectWarning {
  kind:
    | 'moveBook'
    | 'moveDraft'
    | 'primaryBookSkipped'
    | 'primaryDraftSkipped'
    | 'primaryBookInvariant'
    | 'primaryDraftInvariant'
    | 'seedDraft'
    | 'seedDraftInvariant'
    | string;
  message?: string;
  bookId?: number;
  draftId?: number;
  data?: any;
}

/**
 * Success payload shape of `POST /project/createproject`.
 */
export interface CreateProjectResponseData {
  project_ID: number;
  primaryBook_ID?: number;
  primaryDraft_ID?: number;
  movedBook_IDs?: number[];
  movedDraft_IDs?: number[];
  seededDraft_ID?: number | null;
  warnings?: CreateProjectWarning[];
}

/**
 * Full `{message, status, data}` envelope returned by the server
 * for `POST /project/createproject`. `status: 'SUCCESS'` is
 * emitted even on partial success — callers must inspect
 * `data.warnings[]` before treating the Project as fully
 * populated. `status: 'ERROR'` is reserved for early validation
 * failures and the "Project was created but could not be
 * re-loaded" corruption case.
 */
export interface CreateProjectResponseEnvelope {
  message: string;
  status: 'SUCCESS' | 'ERROR' | string;
  data?: CreateProjectResponseData;
}

/**
 * Envelope returned by `PUT /project/{id}/folder`.
 *
 * On refusal the server puts the value it actually has stored in
 * `data.folder`, which is the useful part: a dialog whose `currentFolder`
 * went stale can re-render against the real value instead of re-fetching
 * the Project. `status` is 'SUCCESS' for a genuine move AND for a no-op
 * move (destination already equals the stored value).
 */
export interface SetProjectFolderResponseEnvelope {
  message?: string;
  status: 'SUCCESS' | 'ERROR' | string;
  data?: {
    project_ID?: number;
    folder?: string;
  };
}


@Injectable()
export class ProjectService extends AbstractLiveDataService<Project> {

  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    this.notificationsService.setProjectService(this);
    this.notificationsService.startNotification();
  }

  makeObjectInstance(args: any): Project {
    return new Project(args);
  }

  getLoggingObjectTypeName (): string {
    return "Project";
  }

  supportsParallel(): boolean {
    return false;
  }

  getURLEndPoint (): string {
    return "project";
  }

  /**
   * The two `AddUpdateArray` entries below fire on nothing: `GET /project`
   * nests neither `books` nor `drafts` (`Projects.all()`, `filter()` and
   * `one()` are all bare finds - confirmed by the server 2026-10-17).
   *
   * `project.books` / `project.drafts` are nonetheless populated, from the
   * OTHER side: every Book and Draft links itself onto its Project via
   * `work_ID` as it loads (see `BookService`/`DraftService.getLinkData()`).
   * So do not read an empty array here as "no data" - read it as "none of
   * that Project's items have loaded yet".
   *
   * The practical trap: a Book only reaches the browser nested under
   * `product.book`, because `BookService.getItems()` is called from
   * nowhere. A Book with no Product therefore never loads, and these
   * arrays are silently TRUNCATED to the Product-having population rather
   * than being visibly empty. Pending a paginated `Books.filter()` +
   * startup `BookService.getItems()`, treat any count derived from these
   * arrays as a lower bound.
   */
  getLinkData(): any[] {
    return [
      { action: 'AddUpdateArray', serviceName: 'bookService', arrayProperty: 'books'},
      { action: 'AddUpdateArray', serviceName: 'draftService', arrayProperty: 'drafts'},
      { action: 'Link', objectIdPropertyName: 'primaryBook_ID', serviceName: 'bookService', arrayProperty: 'primaryInProject', objectProperty: 'primaryBook'},
      { action: 'Link', objectIdPropertyName: 'primaryDraft_ID', serviceName: 'draftService', arrayProperty: 'primaryInProject', objectProperty: 'primaryDraft'},
    ];
  }

  /**
   * Dispatch an {@link EndpointCall} produced by {@link ProjectEndpoints}.
   *
   * The verb travels WITH the url rather than being chosen at the call site.
   * `deleteProject` was previously written as a `this.http.post(...)` against a
   * route the server registers as DELETE; no route matched, `handleErrorX`
   * swallowed the 404, and the UI behaved as though the Project had been
   * deleted. Deriving both from one object removes that whole class of
   * mismatch, and lets tests/ProjectUrlShape.smoke.ts assert the wire format
   * with no Angular, no DI and no server.
   */
  private send(action: string, call: EndpointCall): Observable<string> {
    const options = this.startupService.getHttpOptions();
    let request: Observable<any>;
    switch ( call.verb ) {
      case 'DELETE': request = this.http.delete(call.url, options); break;
      case 'PUT':    request = this.http.put(call.url, {}, options); break;
      case 'GET':    request = this.http.get(call.url, options); break;
      default:       request = this.http.post(call.url, {}, options); break;
    }
    return request.pipe(map(response => {
      return response;
    }), catchError(this.handleErrorX<any>(action)));
  }

// setBookAsPrimary - projectId, bookId
  public setBookAsPrimary(projectId: number, bookId: number): Observable<string> {
    return this.send('setbookasprimary', ProjectEndpoints.setBookAsPrimary(environment.apiBaseURL, projectId, bookId));
  }

  // moveBookToProject - bookId, currentProjectId, newProjectId
  public moveBookToProject(bookId: number, currentProjectId: number, newProjectId): Observable<string> {
    return this.send('movebooktoproject', ProjectEndpoints.moveBookToProject(environment.apiBaseURL, bookId, currentProjectId, newProjectId));
  }

  // setDraftAsPrimary - projectId, draftId
  public setDraftAsPrimary(projectId, draftId: number): Observable<string> {
    return this.send('setdraftasprimary', ProjectEndpoints.setDraftAsPrimary(environment.apiBaseURL, projectId, draftId));
  }
  // moveDraftToProject - draftId, currentProjectId, newProjectId
  public moveDraftToProject(draftId: number, currentProjectId: number, newProjectId: number): Observable<string> {
    return this.send('movedrafttoproject', ProjectEndpoints.moveDraftToProject(environment.apiBaseURL, draftId, currentProjectId, newProjectId));
  }

  // formProjects - orgId, (other argument related to the level of algorithm and rules) -- automatically pair up and match based on title and resources
  public formProjects(orgId: number, args: string): Observable<string> {
    return this.send('formProjects', ProjectEndpoints.formProjects(environment.apiBaseURL, orgId, args));
  }

  // createNewProject - projectName
  public createNewProject(projectName: string, folder: string): Observable<string> {
    return this.send('createnewproject', ProjectEndpoints.createNewProject(environment.apiBaseURL, projectName, folder));
  }

  /**
   * Consolidated Project provisioning endpoint. Collapses the legacy
   * `createnewproject → move* → set*asprimary` fan-out into a single
   * POST — see inbox note
   * `2026-08-04-consolidated-createproject-endpoint.md`.
   *
   * The server returns the `{message, status, data}` envelope. `data`
   * carries the assigned Project.ID plus the moved/seeded IDs, and a
   * `warnings[]` array populated on partial success. Callers MUST
   * inspect `warnings[]` before treating the Project as fully
   * populated — the server keeps `status: 'SUCCESS'` even when
   * individual moves failed. Reserved `status: 'ERROR'` covers only
   * early validation failures (missing name, wrong role scope) and
   * the "Project was created but could not be re-loaded" corruption
   * case.
   *
   * Not routed through `send()`: this one carries a JSON body and must
   * deliberately NOT swallow HTTP errors.
   */
  public createProject(req: CreateProjectRequest): Observable<CreateProjectResponseEnvelope> {
    const url = ProjectEndpoints.createProject(environment.apiBaseURL).url;
    return this.http.post<CreateProjectResponseEnvelope>(url, req, this.startupService.getHttpOptions()).pipe(
      map(response => response),
      // Do NOT swallow HTTP errors here: let callers observe status codes
      // (eg. 409 name-collision) so they can react (fallback to add-to-existing).
      catchError((err) => {
        console.error('createProject HTTP error', err);
        return throwError(err);
      })
    );
  }
  // createNewProjectWithDraft - draftId, projectName (optional) -- remove from its current project
  // `projectName` MUST be base64: the server decodes it with
  // `Buffer.from(req.params.projectName, 'base64')` and its route pattern is
  // `([A-Za-z0-9+/=]+)`. Sending it raw meant any title containing a space
  // failed to match the route at all (404) while a space-free title was
  // base64-decoded into mojibake and stored as the Project name.
  public createNewProjectWithDraft(draftId: number, projectName: string): Observable<string> {
    return this.send('createnewprojectwithdraft', ProjectEndpoints.createNewProjectWithDraft(environment.apiBaseURL, draftId, projectName));
  }
  // createNewProjectWithBook - bookId, projectName (optional) -- remove from its current project
  // See createNewProjectWithDraft above: `projectName` is base64 on the wire.
  public createNewProjectWithBook(bookId: number, projectName: string): Observable<string> {
    return this.send('createnewprojectwithbook', ProjectEndpoints.createNewProjectWithBook(environment.apiBaseURL, bookId, projectName));
  }

  // mergeProjects - primaryProjectId, obsoletingProjectId, primaryBookId, primaryDraftId, projectName (optional) -- identify which will be primary Draft and Book if more than one option
  // `projectName` is base64 on the wire, same as the create* endpoints above.
  public mergeProjects(primaryProjectId: number, obsoletingProjectId: number, primaryBookId: number, primaryDraftId: number, projectName: string): Observable<string> {
    return this.send('mergeprojects', ProjectEndpoints.mergeProjects(environment.apiBaseURL, primaryProjectId, obsoletingProjectId, primaryBookId, primaryDraftId, projectName));
  }

  // makeDraftIntoBook - draftId
  public makeDraftIntoBook(draftId: number, makePrimary: boolean): Observable<string> {
    return this.send('makedraftintobook', ProjectEndpoints.makeDraftIntoBook(environment.apiBaseURL, draftId, makePrimary));
  }
  // makeBookIntoDraft - bookId
  public makeBookIntoDraft(bookId: number, makePrimary: boolean): Observable<string> {
    return this.send('makebookintodraft', ProjectEndpoints.makeBookIntoDraft(environment.apiBaseURL, bookId, makePrimary));
  }

  // deleteProject - projectId onlyDeleteIfEmptyProject (boolean) -- delete project including all Drafts and Books?
  //
  // Verb is DELETE, not POST. The server registers this as
  // `router.delete('/deleteproject/:projectId([0-9]+)/:onlyDeleteIfEmptyProject([0-1])')`
  // (src/handlers/express/ProjectRoutes.ts). Issuing it as a POST — which this
  // did — matched no route at all, so BOTH delete-project variants were dead:
  // the request 404'd and the caller's `.subscribe()` saw the error swallowed
  // by handleErrorX, leaving the UI looking like the delete had worked.
  public deleteProject(projectId: number, onlyDeleteIfEmptyProject: boolean): Observable<string> {
    return this.send('deleteproject', ProjectEndpoints.deleteProject(environment.apiBaseURL, projectId, onlyDeleteIfEmptyProject));
  }

  // moveFolders - projectId, currentFolder, newFolder
  //
  // Superseded for single-Project moves by `setProjectFolder` below. Retained
  // because deleting a working route buys nothing, and because the server
  // widened its path pattern to `(*)` on 2026-09-02 so it is no longer broken
  // at the root either.
  public moveFolders(projectId: number, currentFolder: string, newFolder: string): Observable<string> {
    return this.send('movefolders', ProjectEndpoints.moveFolders(environment.apiBaseURL, projectId, currentFolder, newFolder));
  }

  /**
   * Move one Project into `folder` via `PUT /project/{id}/folder`.
   *
   * Not routed through `send()`: this is the only Project call that carries a
   * request body, and that is the whole point of it. Every other Project route
   * spells its arguments into the URL, which is exactly why the root folder --
   * the empty string -- could not be expressed on any of them.
   *
   * `folder` and `currentFolder` are PLAIN strings. Not base64. Do not
   * normalize `currentFolder`: the server compares it RAW against the stored
   * value, so turning a stored '' into '/' would fail the concurrency check for
   * every Project sitting at the root, which is the case this endpoint exists
   * to serve. Pass `undefined` to make the write unconditional.
   *
   * Errors are deliberately NOT swallowed. `handleErrorX` turns a transport
   * failure into `of(undefined)`, which a subscriber cannot tell from success,
   * and a move that silently did not happen is the precise failure shape this
   * endpoint was introduced to remove. Callers must handle the error branch.
   */
  public setProjectFolder(projectId: number, folder: string, currentFolder?: string): Observable<SetProjectFolderResponseEnvelope> {
    const call = ProjectEndpoints.setProjectFolder(environment.apiBaseURL, projectId);
    const body: { folder: string, currentFolder?: string } = { folder: folder };
    if ( currentFolder !== undefined && currentFolder !== null ) {
      body.currentFolder = currentFolder;
    }
    return this.http.put<SetProjectFolderResponseEnvelope>(call.url, body, this.startupService.getHttpOptions()).pipe(
      map(response => response),
      catchError((err) => {
        console.error('setProjectFolder HTTP error', err);
        return throwError(err);
      })
    );
  }

  // moveProjectsInFolder - currentFolder, newFolder -- change all projects in a folder to a new one (return need to refresh if too many objects were changed)
  // Double base64: the server decodes twice (ProjectRoutes.ts moveprojectsinfolder).
  public moveProjectsInFolder(currentFolder: string, newFolder: string): Observable<string> {
    return this.send('moveprojectsinfolder', ProjectEndpoints.moveProjectsInFolder(environment.apiBaseURL, currentFolder, newFolder));
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleErrorX<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
